import type { FooterConfig, FooterContentPart } from "./types";
import { ROOT_ATTR, type FooterInsert } from "./types";
import { buildContentParts, escapeHtml } from "./display";
import { buildLinkClassNames, resolveLabelPlacements } from "./layout";
import { getFooterStyles, normalizeBackgroundColor, resolveFooterSurfaceBackground, resolveSchemeBackground, resolveTheme } from "./styles";

function isPhrasingTarget(element: HTMLElement): boolean {
  return /^(P|SPAN|A|LABEL|LI|DT|DD|H[1-6]|SMALL|STRONG|EM|CITE|Q)$/.test(element.tagName);
}

function resolveMountStrategy(
  config: FooterConfig,
  target: HTMLElement
): { insert: FooterInsert; hostTag: "span" | "div"; mount: "inline-append" | "inline-sibling" | "block" } {
  const requestedInsert = config.insert ?? "append";
  const hasSeparator = Boolean(config.separator?.trim());
  const phrasing = isPhrasingTarget(target);

  if (hasSeparator && phrasing) {
    return { insert: "append", hostTag: "span", mount: "inline-append" };
  }

  if (requestedInsert === "append" && phrasing) {
    return { insert: "append", hostTag: "span", mount: "inline-append" };
  }

  if (requestedInsert === "after" || requestedInsert === "before") {
    return { insert: requestedInsert, hostTag: "span", mount: "inline-sibling" };
  }

  return { insert: "append", hostTag: "div", mount: "block" };
}

function prepareInlineSiblingContainer(target: HTMLElement, insert: FooterInsert): void {
  if (insert !== "after" || !target.classList.contains("powered-by-logo")) {
    return;
  }

  const parent = target.parentElement;
  if (!parent) {
    return;
  }

  parent.style.display = "inline-flex";
  parent.style.flexWrap = "wrap";
  parent.style.alignItems = "center";
  parent.style.justifyContent = "center";
  parent.style.columnGap = "0.65em";
  parent.style.rowGap = "0";
}

function mountHost(host: HTMLElement, target: HTMLElement, insert: FooterInsert = "append"): void {
  if (insert === "after") {
    target.insertAdjacentElement("afterend", host);
    return;
  }

  if (insert === "before") {
    target.insertAdjacentElement("beforebegin", host);
    return;
  }

  target.appendChild(host);
}

function applyHostLayout(
  host: HTMLElement,
  mount: "inline-append" | "inline-sibling" | "block",
  insert?: FooterInsert,
  marginTop?: string
): void {
  host.style.boxSizing = "border-box";
  host.setAttribute("data-mount", mount);

  if (marginTop?.trim()) {
    host.style.marginTop = marginTop.trim();
  }

  if (mount === "inline-append" || mount === "inline-sibling") {
    if (insert) {
      host.setAttribute("data-insert", insert);
    }
    host.style.display = "inline-flex";
    host.style.width = "auto";
    host.style.maxWidth = "none";
    host.style.flexBasis = "auto";
    host.style.verticalAlign = "middle";
    return;
  }

  host.style.display = "block";
  host.style.width = "100%";
  host.style.maxWidth = "100%";
  host.style.flexBasis = "100%";
}

function applyHostSurfaceBackground(
  host: HTMLElement,
  target: HTMLElement,
  config: FooterConfig,
  theme: "light" | "dark",
  mount: "inline-append" | "inline-sibling" | "block"
): void {
  if (config.position === "fixed" || mount !== "block") {
    return;
  }

  const background =
    normalizeBackgroundColor(config.backgroundColor) ??
    resolveSchemeBackground(target) ??
    resolveFooterSurfaceBackground(target, theme);

  if (background) {
    host.style.backgroundColor = background;
  }
}

function findTarget(targetSelector: string): HTMLElement | null {
  const selectors = targetSelector
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  for (const selector of selectors) {
    const node = document.querySelector(selector);
    if (node instanceof HTMLElement) {
      return node;
    }
  }

  return document.body instanceof HTMLElement ? document.body : null;
}

function renderPart(part: FooterContentPart): string {
  if (part.type === "text") {
    const modifier = part.placement === "before" ? " pf-text--before" : " pf-text--after";
    return `<span class="pf-text${modifier}">${escapeHtml(part.text)}</span>`;
  }

  const className =
    part.type === "logo"
      ? "pf-logo"
      : part.type === "icon"
        ? "pf-logo pf-logo--icon"
        : "pf-avatar";

  return `<img class="${className}" src="${escapeHtml(part.src)}" alt="${escapeHtml(part.alt)}" loading="lazy" decoding="async" />`;
}

function buildComposedContent(parts: FooterContentPart[]): string {
  const beforeParts = parts.filter(
    (part) => part.type === "text" && part.placement === "before"
  );
  const brandParts = parts.filter((part) => part.type !== "text");
  const afterParts = parts.filter(
    (part) => part.type === "text" && part.placement === "after"
  );

  const chunks: string[] = [];

  if (beforeParts.length > 0) {
    chunks.push(
      `<span class="pf-slot pf-slot--before">${beforeParts.map(renderPart).join("")}</span>`
    );
  }

  if (brandParts.length > 0) {
    chunks.push(
      `<span class="pf-slot pf-slot--brand">${brandParts.map(renderPart).join("")}</span>`
    );
  }

  if (afterParts.length > 0) {
    chunks.push(
      `<span class="pf-slot pf-slot--after">${afterParts.map(renderPart).join("")}</span>`
    );
  }

  return chunks.join("");
}

function buildMarkup(config: FooterConfig, theme: "light" | "dark"): string {
  const wrapClass =
    config.position === "fixed" ? "pf-wrap pf-wrap--fixed" : "pf-wrap pf-wrap--inline";

  const parts = buildContentParts(config, theme);
  const placements = resolveLabelPlacements(config);

  if (parts.length === 0) {
    return "";
  }

  const linkClass = buildLinkClassNames(placements);
  const inner = buildComposedContent(parts);
  const separator =
    config.separator?.trim() ?
      `<span class="pf-separator" aria-hidden="true">${escapeHtml(config.separator.trim())}</span>`
    : "";

  return `
    <div class="${wrapClass}">
      <a class="${linkClass}" href="${escapeHtml(config.link)}" target="_blank" rel="noopener noreferrer">
        ${separator}${inner}
      </a>
    </div>
  `;
}

export function renderFooter(config: FooterConfig): boolean {
  if (!config.enabled) {
    return false;
  }

  if (document.querySelector(`[${ROOT_ATTR}]`)) {
    return false;
  }

  const target = findTarget(config.target);
  if (!target) {
    console.warn("[PandoriumFooter] Container alvo nao encontrado.");
    return false;
  }

  const theme = resolveTheme(config.theme, target);
  const markup = buildMarkup(config, theme);
  const mountStrategy = resolveMountStrategy(config, target);

  if (!markup) {
    console.warn("[PandoriumFooter] Nenhum conteudo para renderizar.");
    return false;
  }

  const placements = resolveLabelPlacements(config);
  const host = document.createElement(mountStrategy.hostTag);
  host.setAttribute(ROOT_ATTR, "true");
  host.setAttribute("data-platform", config.platform);
  host.setAttribute("data-mode", config.mode);
  host.setAttribute("data-theme", theme);
  if (config.clientId) {
    host.setAttribute("data-client-id", config.clientId);
  }
  if (placements.before) {
    host.setAttribute("data-label-before-placement", placements.before);
  }
  if (placements.after) {
    host.setAttribute("data-label-after-placement", placements.after);
  }

  applyHostLayout(host, mountStrategy.mount, mountStrategy.insert, config.marginTop);
  applyHostSurfaceBackground(host, target, config, theme, mountStrategy.mount);

  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = getFooterStyles(theme);
  shadow.appendChild(style);

  const wrapper = document.createElement("div");
  wrapper.innerHTML = markup;
  shadow.appendChild(wrapper.firstElementChild ?? wrapper);

  if (config.position === "fixed") {
    document.body.appendChild(host);
  } else {
    prepareInlineSiblingContainer(target, mountStrategy.insert);
    mountHost(host, target, mountStrategy.insert);
  }

  return true;
}
